'use client';

import { useState } from "react";
import SlideshowIntro from "@/app/__to_be_integrated/_____components/_NEW/intros/slideshow/SlideshowIntro";
import SnapSectionsContainer from "@/app/features/shared/snap-section/SnapSectionsContainer";
import { DetailSectionEditable } from "@/app/features/shared/detail-section";
import EditableMarkdownV2 from "@/app/features/shared/editable-markdown/EditableMarkdownV2";
import VisiblyScrollableV2 from "@/app/features/shared/__to_be_integrated/ui/v2/VisiblyScrollable";
import type { DetailEditable } from "@/app/features/shared/types/types";
import type { MediaRef } from "@/app/features/shared/__to_be_integrated/types/general/media/media";
import { useSiteResearchDetailEditor } from "@/app/features/sites/research/contexts/SiteResearchDetailEditorContext";
import type { ResearchDetailEditable as ResearchDetailEditableType } from "@/app/features/sites/research/types/types";
import {
    ResearchDetailHeroFrame,
    ResearchDetailPublicationsFrame,
    ResearchDetailSectionFrame,
    ResearchDetailSectionsFrame,
} from "@/app/features/sites/research/ui/shared/ResearchDetailFrames";

export default function ResearchDetailEditable() {
    const { editable, updateEditable } = useSiteResearchDetailEditor();
    const [isPublicationsEditing, setIsPublicationsEditing] = useState(false);

    if (!editable) return null;

    const details = editable.details ?? [];
    const promoImages = details
        .map(detail => detail.image)
        .filter((image): image is MediaRef => image != null);

    function updateDetailByIndex(indexToUpdate: number, partialDetail: Partial<DetailEditable>) {
        updateEditable({
            details: details.map((detail, index) => (
                index === indexToUpdate ? { ...detail, ...partialDetail } : detail
            )) as ResearchDetailEditableType['details'],
        });
    }

    return (
        <SnapSectionsContainer>
            <ResearchDetailHeroFrame>
                <SlideshowIntro
                    title={editable.title}
                    subtitle={editable.subtitle ?? undefined}
                    images={promoImages}
                    isEditing
                    onTitleChanged={title => updateEditable({ title })}
                    onSubtitleChanged={subtitle => updateEditable({ subtitle })}
                />
            </ResearchDetailHeroFrame>

            <ResearchDetailSectionsFrame>
                {details.map((detail, index) => (
                    <ResearchDetailSectionFrame key={detail.id}>
                        <DetailSectionEditable
                            data={detail}
                            alignment={index % 2 === 0 ? 'row' : 'row-reverse'}
                            onTitleChange={title => updateDetailByIndex(index, { title })}
                            onSubtitleChange={subtitle => updateDetailByIndex(index, { subtitle })}
                            onDescriptionChange={description => updateDetailByIndex(index, { description })}
                            onImageChange={image => updateDetailByIndex(index, { image })}
                        />
                    </ResearchDetailSectionFrame>
                ))}
            </ResearchDetailSectionsFrame>

            <ResearchDetailPublicationsFrame>
                <VisiblyScrollableV2
                    scrollDisabled={isPublicationsEditing}
                    scrollContainerAriaLabel="Publikationsliste"
                >
                    <EditableMarkdownV2
                        label="Publikationen"
                        value={editable.publications ?? ''}
                        onChange={publications => updateEditable({ publications })}
                        onEditingChange={setIsPublicationsEditing}
                        ariaLabel="Publikationsliste bearbeiten"
                    />
                </VisiblyScrollableV2>
            </ResearchDetailPublicationsFrame>
        </SnapSectionsContainer>
    );
}
